<template>
    <div :class="[outerClass, colSize, { 'form-field-disabled': isFormDisabled }]">
        <label v-if="label"
               class="block"
               :class="{ 'required-label': hasRequiredAttr, [labelClass]: true }"
               :style="labelStyle">{{
                __(label)
            }}</label>
        <MultiSelect optionLabel="label"
                     optionValue="value"
                     :filter="isFilter"
                     display="chip"
                     class="w-full"
                     :showToggleAll="showClear"
                     :selectionLimit="limit"
                     :loading="isLoading"
                     :disabled="isFormDisabled"
                     :placeholder="placeholder"
                     :virtualScrollerOptions="{ itemSize: 38 }"
                     :name="fieldName"
                     :validation="validation"
                     :options="options"
                     :class="{ 'required-field': hasRequiredAttr }"
                     @filter="filter"
                     v-model="field"
                     :pt="multiSelectPt">
            <template #option="slotProps">
                <span v-html="slotProps.option.label"></span>
            </template>
            <template #value="slotProps">
                <span v-if="Array.isArray(slotProps.value) && slotProps.value.length"
                      class="p-multiselect-token-container">
                    <span v-for="val in slotProps.value"
                          :key="val"
                          class="p-multiselect-token"
                          style="margin-right: 4px; display: inline-block;">
                        <span v-html="options.find(opt => opt.value === val)?.label"></span>
                        <span class="p-multiselect-token-icon pi pi-times"
                              style="margin-left: 6px; cursor: pointer;"
                              @click.stop="removeValue(val)"></span>
                    </span>
                </span>
                <span v-else>&nbsp;</span>
            </template>
        </MultiSelect>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import MultiSelect from 'primevue/multiselect'
import { __, translatePrimeVueMsgs } from '@pages/Helper/utils.js'

translatePrimeVueMsgs();

const props = defineProps({
    fieldName: {
        type: String,
        required: true,
    },
    validation: {
        type: [Array, String],
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: null
    },
    isFocus: {
        type: Boolean,
        default: false
    },
    isFormDisabled: {
        type: Boolean,
        default: false
    },
    isLoading: {
        type: Boolean,
        default: false
    },
    options: {
        type: Array,
        default: [],
        required: true
    },
    filter: {
        type: Function,
        required: false,
    },
    limit: {
        type: Number,
        default: null
    },
    showClear: {
        type: Boolean,
        default: true
    },
    isFilter: {
        type: Boolean,
        default: true
    },
    labelClass: {
        type: String,
        default: null
    },
    labelStyle: {
        type: [Object, String],
        default: null
    },
    inRepeater: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: "\u200B"
    },
});

const field = defineModel('field');
const validation = ref(Array.isArray(props.validation) ? props.validation : [props.validation]);
const colSize = props?.size ? 'col-' + props.size : 'col';
const hasRequiredAttr = validation.value.includes('required');
const outerClass = ref('field');

const multiSelectPt = {
    root: ({ state }) => ({
        class: [
            'w-full min-h-[2.5rem] flex items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-sm shadow-sm transition',
            'border-gray-300 text-gray-900',
            'focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200',
            'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed',
            state?.overlayVisible ? 'ring-2 ring-blue-200 border-blue-500' : ''
        ],
        name: props.fieldName
    }),
    labelContainer: { class: 'flex-1 min-w-0' },
    label: { class: 'flex flex-wrap gap-1 items-center whitespace-normal leading-5' },
    chipItem: { class: 'mr-1 mb-1' },
    pcChip: {
        root: { class: 'inline-flex items-center rounded-md bg-gray-100 text-gray-800 px-2 py-0.5 text-xs font-medium' },
        label: { class: 'leading-4' },
        removeIcon: { class: 'ml-1 text-gray-400 hover:text-gray-600 cursor-pointer' }
    },
    dropdown: { class: 'ml-auto inline-flex items-center justify-center text-gray-500' },
    dropdownIcon: { class: 'text-gray-500' },
    clearIcon: { class: 'text-gray-400 hover:text-gray-600 cursor-pointer' },
    overlay: { class: 'mt-1 rounded-md border border-gray-200 bg-white shadow-lg' },
    header: { class: 'flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-white' },
    pcHeaderCheckbox: {
        root: 'relative inline-flex align-bottom w-5 h-5 cursor-pointer select-none',
        input: 'peer absolute top-0 left-0 w-full h-full p-0 m-0 opacity-0 z-10 rounded cursor-pointer',
        box: 'flex items-center justify-center w-5 h-5 border rounded-md transition-colors border-gray-300 bg-white peer-hover:border-gray-400 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-200 peer-checked:bg-primary-500 peer-checked:border-blue-500',
        icon: 'w-3 h-3 text-white'
    },
    pcFilterContainer: {
        root: { class: 'relative flex-1' }
    },
    pcFilter: {
        root: {
            class: 'w-full rounded-md border border-gray-200 bg-white pl-3 pr-10 py-2 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
        }
    },
    pcFilterIconContainer: {
        root: { class: 'absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none' }
    },
    filterIcon: { class: 'w-4 h-4' },
    listContainer: { class: 'max-h-64 overflow-auto' },
    list: { class: 'py-1' },
    pcOptionCheckbox: {
        root: 'relative inline-flex align-bottom w-5 h-5 cursor-pointer select-none',
        input: 'peer absolute top-0 left-0 w-full h-full p-0 m-0 opacity-0 z-10 rounded cursor-pointer',
        box: 'flex items-center justify-center w-5 h-5 border rounded-md transition-colors border-gray-300 bg-white peer-hover:border-gray-400 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-200 peer-checked:bg-primary-500 peer-checked:border-blue-500',
        icon: 'w-3 h-3 text-white'
    },
    option: ({ context }) => ({
        class: [
            'px-3 py-2 text-sm cursor-pointer select-none flex items-center gap-2',
            context?.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50',
            context?.focused ? 'bg-gray-50' : '',
            context?.selected ? 'bg-primary-50 text-blue-700' : 'text-gray-700'
        ]
    }),
    optionLabel: { class: 'flex-1' },
    emptyMessage: { class: 'px-3 py-2 text-sm text-gray-500' }
}

if (props.inRepeater) {
    outerClass.value = null;
}

const removeValue = (val) => {
    field.value = field.value.filter(v => v !== val);
};

</script>

<style lang="scss" scoped>
:deep(.stacked-items .p-multiselect-items .p-multiselect-item) {
    display: block;
}

:deep(.dynamic-height .p-multiselect-panel .p-multiselect-items) {
    height: auto;
    max-height: 200px;
    overflow-y: auto;
}

.form-field-disabled {

    // Nadajemy szare tło na główny kontener Multiselect (obejmujący ew. przyciski i input)
    :deep([data-pc-name="multiselect"]) {
        background-color: #f5f6f8 !important;

        // Ustawiamy tło samego pola tekstowego na przezroczyste
        input {
            background-color: transparent !important;
        }
    }
}

.p-multiselect-token {
    background: #f1f1f1;
    border-radius: 12px;
    padding: 2px 8px;
    margin-right: 4px;
    display: inline-block;
    font-size: 0.95em;
}
</style>
